import { useTranslation } from 'react-i18next';
import { InvitationDto } from 'woofwoof-api';
import { getOrganizationOpeningStatus } from 'src/utils/getOrganizationOpeningStatus';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import { useInvitationClinic } from './useInvitationClinic';

const ROLE_META: Record<string, { key: string; icon: string }> = {
  ORGANIZATION_OWNER: { key: 'owner', icon: 'solar:crown-bold-duotone' },
  ORGANIZATION_ADMIN: { key: 'admin', icon: 'solar:shield-user-bold-duotone' },
  PROFESSIONAL: {
    key: 'professional',
    icon: 'solar:stethoscope-bold-duotone',
  },
};
const DAY_MS = 86_400_000;

export interface InvitationRole {
  key: string;
  icon: string;
  label: string;
  description: string;
  perks: string[];
}

export interface InvitationDetails {
  name: string;
  initials: string;
  logoUrl?: string;
  photo?: string;
  phoneNumber?: string;
  email?: string;
  website?: string;
  city?: string;
  address?: string;
  opening?: { isOpen: boolean; text: string; hours: string };
  /** Monday-first; true when the clinic has any opening hours that day. */
  openDays: boolean[];
  todayIndex: number;
  specializations: string[];
  roles: InvitationRole[];
  expiresAt: string;
  daysLeft: number;
  validity: string;
  isLoading: boolean;
}

export function useInvitationDetails(
  invitation: InvitationDto,
): InvitationDetails {
  const { t } = useTranslation();
  const { data: clinic, isLoading } = useInvitationClinic(
    invitation.organizationId,
  );
  const name =
    invitation.organizationName ||
    clinic?.name ||
    String(t('invitationAcceptance.organization'));
  const status = clinic?.workingHours?.length
    ? getOrganizationOpeningStatus(clinic.workingHours, clinic.timeOff)
    : undefined;
  const msLeft = Date.parse(invitation.expiresAt) - Date.now();
  const daysLeft = Math.max(0, Math.floor(msLeft / DAY_MS));
  const hoursLeft = Math.max(1, Math.ceil(msLeft / 3_600_000));

  return {
    name,
    initials:
      name
        .split(/\s+/)
        .filter((word) => /^\p{Lu}/u.test(word))
        .slice(-2)
        .map((word) => word[0])
        .join('') || name.slice(0, 2).toUpperCase(),
    logoUrl: clinic?.logoUrl || undefined,
    photo: clinic?.images?.[0],
    phoneNumber: clinic?.phoneNumber || undefined,
    email: clinic?.email || undefined,
    website: clinic?.website || undefined,
    city: clinic?.city,
    address:
      clinic &&
      ([clinic.street, [clinic.zip, clinic.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ') ||
        undefined),
    opening: status && {
      isOpen: status.isOpen,
      text: status.text,
      hours: status.shortTextHours,
    },
    openDays: [1, 2, 3, 4, 5, 6, 0].map((weekday) =>
      Boolean(clinic?.workingHours?.some((slot) => slot.weekday === weekday)),
    ),
    todayIndex: (new Date().getDay() + 6) % 7,
    specializations: (clinic?.specializations ?? []).map((item) =>
      mapSpecializationNameIdToString(item.name),
    ),
    roles: Array.from(new Set(invitation.roles)).map((role) => {
      const meta = ROLE_META[role] ?? {
        key: 'other',
        icon: 'solar:user-bold-duotone',
      };
      return {
        ...meta,
        label: String(t(`invitationAcceptance.roles.${meta.key}.label`)),
        description: String(
          t(`invitationAcceptance.roles.${meta.key}.description`),
        ),
        perks: [0, 1, 2]
          .map((index) =>
            String(
              t(`invitationAcceptance.roles.${meta.key}.perks.${index}`, {
                defaultValue: '',
              }),
            ),
          )
          .filter(Boolean),
      };
    }),
    expiresAt: new Intl.DateTimeFormat('pl-PL', {
      day: 'numeric',
      month: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Warsaw',
    }).format(new Date(invitation.expiresAt)),
    daysLeft,
    validity:
      daysLeft >= 2
        ? String(t('invitationAcceptance.validDays', { count: daysLeft }))
        : daysLeft === 1
        ? String(t('invitationAcceptance.validDay'))
        : String(t('invitationAcceptance.validHours', { count: hoursLeft })),
    isLoading,
  };
}
